<template>
  <div class="admin-dashboard">
    <div class="admin-header">
      <h1>관리자 페이지</h1>
      <button class="logout-button" type="button" :disabled="isLoggingOut" @click="handleLogout">
        <font-awesome-icon :icon="['fas', 'right-from-bracket']" aria-hidden="true" />
        <span>{{ isLoggingOut ? '로그아웃 중' : '로그아웃' }}</span>
      </button>
    </div>

    <div class="button-group">
      <button @click="goTo('/admin/notice/new')">📢 공지사항 작성</button>
      <button @click="goTo('/admin/resource/new')">📎 자료실 작성</button>
    </div>

    <div class="content-lists">
      <!-- 공지사항 -->
      <div class="list-section">
        <h2>📢 공지사항</h2>

        <SearchBar @search="handleNoticeSearch" />
        <NoticeTable 
          :notices="paginatedNotices"
          :all-notices="normalizedAllNotices"
          :current-page="noticeCurrentPage"
          :per-page="perPage"
          :total="nonPinnedTotal"
          :admin="true"
          @edit="handleNoticeEdit"
          @delete="handleNoticeDelete"
        />
        <Pagination
          :total="nonPinnedTotal"
          :current-page="noticeCurrentPage"
          :page="noticeCurrentPage"
          :per-page="perPage"
          @change="changeNoticePage"
        />
      </div>

      <!-- 자료실 -->
      <div class="list-section">
        <h2>📎 자료실</h2>

        <SearchBar @search="handleResourceSearch" />
        <ResourcesTable 
          :resources="paginatedResources"
          :admin="true"
          @edit="handleResourceEdit"
          @delete="handleResourceDelete"
        />
        <Pagination
          :total="filteredResources.length"
          :current-page="resourceCurrentPage"
          :page="resourceCurrentPage"
          :per-page="perPage"
          @change="changeResourcePage"
        />
      </div>
    </div>

    <!-- 청약 관리 -->
    <div class="section">
      <h2>🏢 청약 관리</h2>
      <div class="subscribe-controls">
        <div class="current-subscribe-title">📌 현재 청약 확인</div>
        <!-- 검색창 추가 -->
        <SearchBar @search="handleSubscriptionSearch" />
        <!-- ✅ 청약 데이터 존재 여부 체크 -->
        <div v-if="subscriptions.length === 0" class="no-subscription">
          현재 진행중인 청약이 없습니다.
        </div>

        <div v-else class="subscribe-table">
          <table>
            <thead>
              <tr>
                <th>제목</th>
                <th>청약 타입</th>
                <th>공고일</th>
                <th>시작일</th>
                <th>종료일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="subscription in paginatedSubscriptions" :key="subscription.id">
                <tr>
                  <td>{{ subscription.title }}</td>
                  <td>
                    <div v-for="type in subscription.subscriptionType" :key="type">
                          {{ mapSubscriptionTypeLabel(type) }}
                    </div>
                  </td>
                  <td class="date">{{ subscription.noticeAt }}</td>
                  <td class="date">{{ formatDate(subscription.startTs) }}</td>
                  <td class="date">{{ formatDate(subscription.endTs) }}</td>
                  <td class="manage-cell">
                    <button class="edit-button" @click="handleEdit(subscription)">수정</button>
                    <button class="applicants-button" @click="toggleApplicants(subscription.id)">신청자 목록</button>
                    <button
                      class="close-button"
                      :class="{ closed: subscription.closed }"
                      @click="toggleClose(subscription)"
                    >
                      {{ subscription.closed ? '마감 해제' : '마감' }}
                    </button>
                  </td>
                </tr>
                <tr v-if="openedApplicantsId === subscription.id" :key="'applicants-' + subscription.id">
                  <td :colspan="6" style="background:#faf8ff; padding:0; font-size: 10px;">
                    <ApplicantsTable :subscriptionId="subscription.id" />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
                <!-- 페이징 추가 -->
          <Pagination
            :total="filteredSubscriptions.length"
            :current-page="subscriptionCurrentPage"
            :page="subscriptionCurrentPage"
            :per-page="subPerPage"
            @change="changeSubscriptionPage"
          />
        </div>
   
        <li><button @click="goTo('/admin/apply/create')">🚀 청약 시작하기</button></li>


        <li><button @click="goTo('/admin/popup')">🔧 팝업창 관리</button></li>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/utils/axios';
import SearchBar from "@/components/SearchBar.vue";
import Pagination from "@/components/Pagination.vue";
import ResourcesTable from "@/components/ResourcesTable.vue";
import NoticeTable from '@/components/NoticeTable.vue';
import ApplicantsTable from '@/components/ApplicantsTable.vue'


// ✅ (추가) top_fixed → topFixed 정규화 (필터 후 전체 목록 기준)
const normalizedAllNotices = computed(() =>
  (filteredNotices.value || []).map(n => ({
    ...n,
    topFixed: n.top_fixed === true || n.topFixed === true, // 백엔드 키 혼용 대응
  }))
);

// ✅ (추가) 공지 제외한 일반글만 정렬
const nonPinnedSorted = computed(() =>
  normalizedAllNotices.value
    .filter(n => !n.topFixed)
    .sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0))
);

// ✅ (추가) 일반글 총 개수 (페이지네이션/번호용)
const nonPinnedTotal = computed(() => nonPinnedSorted.value.length);

const router = useRouter()
const openedApplicantsId = ref(null);
const isLoggingOut = ref(false);
const perPage = 5

const mapSubscriptionTypeLabel = (type) => {
  switch(type) {
    case 'GENERAL-15':
      return '일반15'
    case 'GENERAL-30':
      return '일반30'
    case 'SPECIAL-15':
      return '특공15'
    default:
      return type
  }
}

const toggleApplicants = (id) => {
  if (openedApplicantsId.value === id) {
    openedApplicantsId.value = null; // 이미 열려있으면 닫기
  } else {
    openedApplicantsId.value = id;   // 해당 ID로 열기
  }
};
// 청약 리스트
const subscriptions = ref([]);

// 공지사항
const notices = ref([])
const noticeKeyword = ref("")
const noticeCurrentPage = ref(1)

// 자료실
const resources = ref([])
const resourceKeyword = ref("")
const resourceCurrentPage = ref(1)

// 페이징 관련
const changeNoticePage = (page) => {
  noticeCurrentPage.value = page
}

const changeResourcePage = (page) => {
  resourceCurrentPage.value = page
}

const goTo = (path) => {
  router.push(path)
}

const handleLogout = async () => {
  if (isLoggingOut.value) return;

  isLoggingOut.value = true;
  try {
    await axios.post('/bx201/admin/logout');
  } catch (e) {
    console.log(e);
  } finally {
    localStorage.removeItem('adminToken');
    isLoggingOut.value = false;
    router.replace('/login');
  }
}

const handleEdit = (subscription) => {
  // 예시: 수정 페이지로 이동
  router.push({ name: 'ApplyEdit', params: { id: subscription.id } });
};

// 마감 상태 토글 함수
const toggleClose = async (subscription) => {
  try {
    // API: PATCH/PUT 등 백엔드에 맞게 수정
    await axios.put(`/bx201/admin/subscription/${subscription.id}/close`, {
      isClosed: !subscription.closed,
    });
    // 성공시 프론트 값 동기화
    subscription.closed = !subscription.closed;
    await fetchLists();
    alert(`청약이 ${subscription.closed ? '마감되었습니다.' : '마감 해제되었습니다.'}`);
  } catch (e) {
    alert('마감 상태 변경에 실패했습니다.');
  }
};

const fetchLists = async () => {
  try {
    const [noticeRes, resourceRes, subscriptionsRes] = await Promise.all([
      axios.get('/bx201/notice'),
      axios.get('/bx201/resource'),
      axios.get('/bx201/subscription')
    ])
    notices.value = noticeRes.data
    resources.value = resourceRes.data
    subscriptions.value = subscriptionsRes.data
  } catch (error) {
    console.log(error)
  }
}

const filteredNotices = computed(() =>
  notices.value.filter(n => n.title.toLowerCase().includes(noticeKeyword.value.toLowerCase()))
)
const filteredResources = computed(() =>
  resources.value.filter(r => r.title.toLowerCase().includes(resourceKeyword.value.toLowerCase()))
)


// ✅ (교체) 기존 paginatedNotices → 일반글 기준으로 perPage 슬라이스
const paginatedNotices = computed(() => {
  const start = (noticeCurrentPage.value - 1) * perPage;
  return nonPinnedSorted.value.slice(start, start + perPage);
});

const paginatedResources = computed(() => {
  const start = (resourceCurrentPage.value - 1) * perPage;
  return filteredResources.value.slice(start, start + perPage);
})

const handleNoticeSearch = (term) => {
  if (term === null) return;
  noticeKeyword.value = term;
  noticeCurrentPage.value = 1;
}

const handleResourceSearch = (term) => {
  if (term === null) return;
  resourceKeyword.value = term;
  resourceCurrentPage.value = 1;
}

const handleNoticeDelete = (notice) => {
  if (confirm('정말 삭제하시겠습니까?')) {
    axios.delete(`/bx201/admin/notice/${notice.id}`)
      .then(() => {
        alert('삭제 완료');
        fetchLists(); // 삭제 후 목록 다시 불러오기
      })
  }
}

const handleResourceDelete = (id) => {
  if (confirm('정말 삭제하시겠습니까?')) {
    axios.delete(`/bx201/admin/resource/${id}`)
      .then(() => {
        alert('삭제 완료');
        fetchLists(); // 삭제 후 목록 다시 불러오기
      })
  }
}

const handleNoticeEdit = (notice) => {
  router.push({
    name: 'NoticeEdit',
    params: { id: notice.id }
  });
}

const handleResourceEdit = (resource) => {
  router.push({
    name: 'ResourceEdit',
    params: { id: resource.id }
  });
}

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');

  const ampm = hours < 12 ? '오전' : '오후';
  hours = hours % 12;
  hours = hours ? hours : 12; // 0시는 12시로 표시

  return `${year}-${month}-${day} ${ampm} ${String(hours).padStart(2, '0')}:${minutes}`;
};

const subscriptionKeyword = ref('');
const subscriptionCurrentPage = ref(1);
const subPerPage = 5;

const handleSubscriptionSearch = (term) => {
  if (term === null) return;
  subscriptionKeyword.value = term;
  subscriptionCurrentPage.value = 1;
}

const changeSubscriptionPage = (page) => {
  subscriptionCurrentPage.value = page;
}

const filteredSubscriptions = computed(() => {
  return subscriptions.value
    .filter(s =>
      s.title.toLowerCase().includes(subscriptionKeyword.value.toLowerCase())
    )
    .sort((a, b) => new Date(b.endTs) - new Date(a.endTs)); // endTs 최신순
});

const paginatedSubscriptions = computed(() => {
  const start = (subscriptionCurrentPage.value - 1) * subPerPage;
  return filteredSubscriptions.value.slice(start, start + subPerPage);
});


onMounted(fetchLists)
</script>

<style scoped>

.admin-dashboard {
  max-width: 1500px;
  margin: 60px auto;
  padding: 30px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

h1 {
  flex: 1;
  text-align: center;
  margin: 0;
  color: #333;
}

.logout-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 112px;
  background-color: #495057;
  white-space: nowrap;
}

.logout-button:hover:not(:disabled) {
  background-color: #343a40;
}

.logout-button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.button-group {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
}

button {
  padding: 10px 16px;
  font-size: 1rem;
  border: none;
  border-radius: 6px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
}

button:hover {
  background-color: #0056b3;
}

.content-lists {
  display: flex;
  gap: 30px;
  justify-content: space-between;
  margin-bottom: 50px;
}

.list-section {
  flex: 1;
}

.list-section h2 {
  margin-bottom: 14px;
  font-size: 1.4rem;
  color: #444;
}

.section {
  margin-top: 50px;
}

.section h2 {
  margin-bottom: 12px;
}

.subscribe-controls {
  list-style: none;
  padding: 0;
}

.subscribe-controls li {
  margin-bottom: 10px;
}

.subscribe-controls button {
  width: 100%;
  text-align: left;
}

.subscribe-table {
  margin-top: 20px;
  padding-bottom: 30px;
}


.subscribe-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.subscribe-table th,
.subscribe-table td {
  padding: 10px;
  min-height: 120px;
  max-height: 100px;
  border: 1px solid #ddd;
  text-align: center;
}

.subscribe-table th {
  background-color: #f7f7f7;
}

.current-subscribe-title {
  font-weight: bold;
  font-size: 1.2rem;
  margin-bottom: 10px;
}

.no-subscription {
  text-align: center;
  padding: 80px 0;
  font-size: 1.2rem;
  color: #666;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #4CAF50;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.manage-cell {
  display: flex;
  justify-content: center;
  align-items: center; /* center면 행이 커져도 버튼은 가운데 */
  gap: 10px;
  padding: 0;   /* 테이블의 패딩이 이미 있음 */
}

.edit-button,
.applicants-button {
  min-width: 90px;
  height: 36px;     /* 버튼 높이 고정 */
  padding: 0 16px;
  font-size: 1rem;
  border-radius: 6px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.edit-button:hover {
  background-color: #0056b3;
}

.applicants-button {
  background-color: #6f42c1;
}
.applicants-button:hover {
  background-color: #5936a6;
}

.content {
  margin-top: 16px;
  line-height: 1.8;
  color: #444;
  font-size: 1rem;
}

.close-button {
  min-width: 70px;
  background-color: #e85d04;
  color: #fff;
  margin-left: 5px;
}
.close-button.closed {
  background-color: #aaa;
  color: #222;
}
.close-button:hover:not(.closed) {
  background-color: #c44700;
}
.close-button.closed:hover {
  background-color: #999;
}

@media (max-width: 900px) {
  .content-lists {
    flex-direction: column;
    gap: 24px;
  }
  .list-section {
    min-width: 0;
    width: 100%;
  }
}

@media (max-width: 768px) {
  .admin-dashboard {
    max-width: 100vw;
    margin: 0;
    padding: 10px 2vw 40px 2vw;
    border-radius: 0;
    box-shadow: none;
  }
  h1 {
    font-size: 1.25rem;
  }
  .admin-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 18px;
  }
  .logout-button {
    width: 100%;
    min-height: 42px;
  }
  .button-group {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 24px;
  }
  .button-group button {
    font-size: 1rem;
    padding: 12px 0;
    width: 100%;
    border-radius: 8px;
  }
  .content-lists {
    flex-direction: column;
    gap: 20px;
    margin-bottom: 32px;
  }
  .list-section h2,
  .section h2 {
    font-size: 1.1rem;
    margin-bottom: 10px;
  }
  .current-subscribe-title {
    font-size: 1rem;
  }
  .no-subscription {
    font-size: 1rem;
    padding: 40px 0;
  }
  .content {
    font-size: 0.96rem;
    margin-top: 8px;
  }
  .list-section {
    padding: 0;
  }
}
@media (max-width: 480px) {
  .admin-dashboard {
    padding: 0 1vw 30px 1vw;
  }
}

.subscribe-table,
.subscribe-table table {
  width: 100%;
  overflow-x: auto;
  font-size: 0.95rem;
}


.subscribe-table table {
  min-width: 540px;
}

.subscribe-table th,
.subscribe-table td {
  padding: 8px 4px;
  font-size: 0.90rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
  min-width: 60px;
}

@media (max-width: 768px) {
  .subscribe-table th,
  .subscribe-table td {
    font-size: 0.85rem;
    max-width: 80px;
    min-width: 50px;
    padding: 6px 2px;
  }
  .subscribe-table table {
    min-width: 410px;
  }
}

@media (max-width: 430px) {
  .subscribe-table th,
  .subscribe-table td {
    font-size: 0.76rem;
    max-width: 64px;
    min-width: 44px;
    padding: 5px 1px;
  }
  .subscribe-table table {
    min-width: 340px;
  }
}

.manage-cell {
  flex-direction: column;
  gap: 7px;
  padding: 0;
}
.edit-button,
.applicants-button,
.close-button {
  width: 100%;
  min-width: 0;
  height: 36px;
  font-size: 0.98rem;
  margin: 0;
  border-radius: 5px;
  padding: 0;
  justify-content: center;
}
@media (max-width: 768px) {
  .edit-button,
  .applicants-button,
  .close-button {
    height: 32px;
    font-size: 0.90rem;
  }

    .subscribe-table th.date,
  .subscribe-table td.date {
    white-space: normal !important;     /* ✅ 줄바꿈 허용 */
    word-break: break-all;              /* ✅ 강제 줄바꿈 */
    text-overflow: clip;                /* ... 안 나옴 */
  }
}
.subscribe-table {
  overflow-x: auto;
}

</style>
